export type VerificationEntityType = 'radio' | 'programa' | 'locutor' | 'patrocinador' | 'empresa';

export type VerificationBadgeLevel = 'verificada' | 'oficial' | 'premium';

export type VerificationStatus = 'activa' | 'pendiente' | 'rechazada' | 'revocada';

export interface VerificationEntityTypeConfig {
  label: string;
  labelPlural: string;
  icon: string;
  color: string;
  bgColor: string;
  description: string;
}

export interface VerificationBadgeConfig {
  label: string;
  icon: string;
  iconBadge: string;
  color: string;
  bgColor: string;
  borderColor: string;
  description: string;
  priority: number;
}

export interface VerifiedEntity {
  id: string;
  entityType: VerificationEntityType;
  entityName: string;
  entitySlug: string;
  entityPhotoUrl: string;
  entityCoverUrl?: string;
  badgeLevel: VerificationBadgeLevel;
  status: VerificationStatus;
  verifiedAt: string;
  verifiedAtRelative: string;
  verifiedBy: string;
  verifiedByRole: string;
  description: string;
  category: string;
  location?: string;
  audience?: string;
  since?: string;
  stats: {
    followers: number;
    rating: number;
    interactions: number;
  };
  benefits: string[];
  auditTrail: {
    requestedBy: string;
    requestedAt: string;
    reviewedBy: string;
    reviewedAt: string;
    grantedBy: string;
    grantedAt: string;
  };
}

export interface VerificationStats {
  totalVerificados: number;
  porRadio: number;
  porPrograma: number;
  porLocutor: number;
  porPatrocinador: number;
  porEmpresa: number;
  premiumCount: number;
  oficialCount: number;
  verificadaCount: number;
  tasaAprobacion: string;
  tiempoMedioVerificacion: string;
  verificacionesHoy: number;
  pendientes: number;
}

export const entityTypeConfig: Record<VerificationEntityType, VerificationEntityTypeConfig> = {
  radio: {
    label: 'Radio',
    labelPlural: 'Radios',
    icon: 'ri-radio-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    description: 'Emisoras verificadas que cumplen con los estándares de calidad, transmisión estable y comunidad activa de RFW',
  },
  programa: {
    label: 'Programa',
    labelPlural: 'Programas',
    icon: 'ri-calendar-check-line',
    color: 'text-sky-600',
    bgColor: 'bg-sky-50',
    description: 'Programas verificados con contenido original, audiencia consistente y calidad de producción profesional',
  },
  locutor: {
    label: 'Locutor',
    labelPlural: 'Locutores',
    icon: 'ri-mic-line',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    description: 'Locutores y hosts verificados con trayectoria comprobable, identidad confirmada y comunidad fiel',
  },
  patrocinador: {
    label: 'Patrocinador',
    labelPlural: 'Patrocinadores',
    icon: 'ri-hand-heart-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    description: 'Patrocinadores verificados con historial de apoyo real a radios, eventos y concursos de RFW',
  },
  empresa: {
    label: 'Empresa',
    labelPlural: 'Empresas',
    icon: 'ri-building-line',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    description: 'Empresas verificadas que operan dentro del ecosistema RFW con servicios y productos legítimos',
  },
};

export const badgeConfig: Record<VerificationBadgeLevel, VerificationBadgeConfig> = {
  verificada: {
    label: 'Verificada',
    icon: 'ri-verified-badge-line',
    iconBadge: 'ri-verified-badge-fill',
    color: 'text-sky-500',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-300',
    description: 'Identidad confirmada. La entidad pasó la verificación básica de autenticidad en RFW.',
    priority: 1,
  },
  oficial: {
    label: 'Oficial',
    icon: 'ri-shield-check-line',
    iconBadge: 'ri-shield-check-fill',
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-300',
    description: 'Verificación avanzada. Entidad oficialmente reconocida con trayectoria comprobada y estándares superiores.',
    priority: 2,
  },
  premium: {
    label: 'Premium',
    icon: 'ri-vip-crown-line',
    iconBadge: 'ri-vip-crown-fill',
    color: 'text-amber-500',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
    description: 'Máximo nivel de verificación. Entidad de élite con excelencia comprobada, comunidad masiva y calidad excepcional.',
    priority: 3,
  },
};

export const verificationStats: VerificationStats = {
  totalVerificados: 4870,
  porRadio: 1240,
  porPrograma: 1580,
  porLocutor: 1350,
  porPatrocinador: 420,
  porEmpresa: 280,
  premiumCount: 312,
  oficialCount: 1258,
  verificadaCount: 3300,
  tasaAprobacion: '94.2%',
  tiempoMedioVerificacion: '3.7 días',
  verificacionesHoy: 47,
  pendientes: 128,
};

export const verifiedEntities: VerifiedEntity[] = [
  // ═══════ RADIOS (6) ═══════
  {
    id: 'ver-radio-001',
    entityType: 'radio',
    entityName: 'La Mega 107.3 FM',
    entitySlug: 'la-mega-107-3',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Professional%20radio%20station%20logo%20design%20bold%20typography%20warm%20golden%20energy%20waves%20abstract%20sound%20frequency%20pattern%20modern%20clean%20brand%20identity&width=200&height=200&seq=ver-lamega&orientation=squarish',
    entityCoverUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20control%20room%20with%20professional%20audio%20equipment%20warm%20ambient%20lighting%20modern%20broadcast%20studio%20environment%20glowing%20meters%20soundboards%20professional%20atmosphere&width=1600&height=600&seq=ver-lamega-cover&orientation=landscape',
    badgeLevel: 'premium',
    status: 'activa',
    verifiedAt: '2025-03-15T10:00:00Z',
    verifiedAtRelative: 'Marzo 2025',
    verifiedBy: 'Ricardo Montero',
    verifiedByRole: 'Super Admin RFW',
    description: 'La emisora líder en música urbana y entretenimiento en Colombia. Más de 2.8 millones de oyentes fieles, programas emblemáticos y la comunidad más activa de RFW.',
    category: 'Música Urbana / Entretenimiento',
    location: 'Bogotá, Colombia',
    audience: '2.8M oyentes semanales',
    since: '2008',
    stats: { followers: 2840000, rating: 4.92, interactions: 15600000 },
    benefits: ['Insignia Premium dorada en todos los espacios', 'Prioridad en rankings y búsquedas', 'Acceso anticipado a nuevas funciones', 'Soporte prioritario 24/7', 'Verificación heredada a programas'],
    auditTrail: {
      requestedBy: 'Gerencia La Mega',
      requestedAt: '2025-02-20',
      reviewedBy: 'Ricardo Montero',
      reviewedAt: '2025-03-10',
      grantedBy: 'Ricardo Montero',
      grantedAt: '2025-03-15',
    },
  },
  {
    id: 'ver-radio-002',
    entityType: 'radio',
    entityName: 'Onda Cero 104.5 FM',
    entitySlug: 'onda-cero-104-5',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Modern%20radio%20station%20logo%20sleek%20typography%20cool%20blue%20tones%20sound%20waves%20abstract%20circular%20design%20contemporary%20brand%20identity%20professional&width=200&height=200&seq=ver-ondacero&orientation=squarish',
    entityCoverUrl: 'https://readdy.ai/api/search-image?query=Professional%20broadcast%20studio%20interior%20modern%20design%20clean%20lines%20warm%20lighting%20radio%20equipment%20soundproofing%20panels%20creative%20workspace%20atmosphere&width=1600&height=600&seq=ver-ondacero-cover&orientation=landscape',
    badgeLevel: 'premium',
    status: 'activa',
    verifiedAt: '2025-05-20T08:30:00Z',
    verifiedAtRelative: 'Mayo 2025',
    verifiedBy: 'Ricardo Montero',
    verifiedByRole: 'Super Admin RFW',
    description: 'La referencia en noticias, deportes y análisis en América Latina. Periodismo de calidad, debates profundos y la mejor cobertura deportiva de la región.',
    category: 'Noticias / Deportes / Actualidad',
    location: 'Medellín, Colombia',
    audience: '2.1M oyentes semanales',
    since: '2010',
    stats: { followers: 2100000, rating: 4.87, interactions: 12800000 },
    benefits: ['Insignia Premium dorada en todos los espacios', 'Prioridad en rankings y búsquedas', 'Acceso anticipado a nuevas funciones', 'Soporte prioritario 24/7'],
    auditTrail: {
      requestedBy: 'Dirección Onda Cero',
      requestedAt: '2025-04-15',
      reviewedBy: 'Ricardo Montero',
      reviewedAt: '2025-05-10',
      grantedBy: 'Ricardo Montero',
      grantedAt: '2025-05-20',
    },
  },
  {
    id: 'ver-radio-003',
    entityType: 'radio',
    entityName: 'Rock & Pop 95.1 FM',
    entitySlug: 'rock-and-pop-95-1',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Bold%20rock%20radio%20station%20logo%20edgy%20typography%20electric%20guitar%20motif%20sound%20wave%20graphic%20dark%20background%20contrast%20modern%20brand%20identity&width=200&height=200&seq=ver-rockpop&orientation=squarish',
    badgeLevel: 'oficial',
    status: 'activa',
    verifiedAt: '2025-07-10T12:00:00Z',
    verifiedAtRelative: 'Julio 2025',
    verifiedBy: 'Carolina Méndez',
    verifiedByRole: 'Super Admin RFW',
    description: 'El hogar del rock en español y el pop alternativo. Conducción irreverente, la mejor música y una comunidad apasionada que respira rock las 24 horas.',
    category: 'Rock / Pop / Alternativo',
    location: 'Buenos Aires, Argentina',
    audience: '890K oyentes semanales',
    since: '2012',
    stats: { followers: 890000, rating: 4.65, interactions: 5400000 },
    benefits: ['Insignia Oficial verde en perfil y cabina', 'Mayor visibilidad en búsquedas', 'Filtro anti-spam mejorado'],
    auditTrail: {
      requestedBy: 'Dirección Rock & Pop',
      requestedAt: '2025-06-01',
      reviewedBy: 'Carolina Méndez',
      reviewedAt: '2025-07-05',
      grantedBy: 'Carolina Méndez',
      grantedAt: '2025-07-10',
    },
  },
  {
    id: 'ver-radio-004',
    entityType: 'radio',
    entityName: 'Tropicalísima 102.7 FM',
    entitySlug: 'tropicalisima-102-7',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Vibrant%20tropical%20radio%20station%20logo%20warm%20sunset%20colors%20palm%20tree%20silhouette%20sound%20waves%20rhythmic%20pattern%20festive%20modern%20brand%20identity&width=200&height=200&seq=ver-tropical&orientation=squarish',
    badgeLevel: 'oficial',
    status: 'activa',
    verifiedAt: '2025-08-22T09:45:00Z',
    verifiedAtRelative: 'Agosto 2025',
    verifiedBy: 'Carolina Méndez',
    verifiedByRole: 'Super Admin RFW',
    description: 'La emisora que pone a bailar a toda Latinoamérica. Salsa, merengue, bachata y los ritmos tropicales más pegados en un solo lugar.',
    category: 'Música Tropical / Salsa / Merengue',
    location: 'Cali, Colombia',
    audience: '750K oyentes semanales',
    since: '2014',
    stats: { followers: 750000, rating: 4.58, interactions: 4200000 },
    benefits: ['Insignia Oficial verde en perfil y cabina', 'Mayor visibilidad en búsquedas'],
    auditTrail: {
      requestedBy: 'Gerencia Tropicalísima',
      requestedAt: '2025-07-20',
      reviewedBy: 'Carolina Méndez',
      reviewedAt: '2025-08-15',
      grantedBy: 'Carolina Méndez',
      grantedAt: '2025-08-22',
    },
  },
  {
    id: 'ver-radio-005',
    entityType: 'radio',
    entityName: 'Radio Felicidad 88.9 FM',
    entitySlug: 'radio-felicidad-88-9',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Warm%20welcoming%20radio%20station%20logo%20soft%20golden%20tones%20heart%20motif%20gentle%20sound%20wave%20design%20comforting%20community%20feel%20modern%20brand%20identity&width=200&height=200&seq=ver-felicidad&orientation=squarish',
    badgeLevel: 'verificada',
    status: 'activa',
    verifiedAt: '2025-10-05T11:30:00Z',
    verifiedAtRelative: 'Octubre 2025',
    verifiedBy: 'Andrés Calle',
    verifiedByRole: 'Super Admin RFW',
    description: 'Música para el alma y contenido positivo. La radio que acompaña tus mañanas con buena energía, consejos de vida y la mejor música de todos los tiempos.',
    category: 'Música Variada / Bienestar',
    location: 'Lima, Perú',
    audience: '480K oyentes semanales',
    since: '2016',
    stats: { followers: 480000, rating: 4.45, interactions: 2800000 },
    benefits: ['Insignia Verificada azul en perfil', 'Confianza para nuevos oyentes'],
    auditTrail: {
      requestedBy: 'Dirección Radio Felicidad',
      requestedAt: '2025-09-10',
      reviewedBy: 'Andrés Calle',
      reviewedAt: '2025-09-28',
      grantedBy: 'Andrés Calle',
      grantedAt: '2025-10-05',
    },
  },
  {
    id: 'ver-radio-006',
    entityType: 'radio',
    entityName: 'La Kalle 96.3 FM',
    entitySlug: 'la-kalle-96-3',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Dynamic%20urban%20radio%20station%20logo%20street%20style%20typography%20graffiti%20inspired%20design%20hip%20hop%20culture%20sound%20wave%20modern%20edgy%20brand%20identity&width=200&height=200&seq=ver-lakalle&orientation=squarish',
    badgeLevel: 'premium',
    status: 'activa',
    verifiedAt: '2025-01-28T10:00:00Z',
    verifiedAtRelative: 'Enero 2025',
    verifiedBy: 'Ricardo Montero',
    verifiedByRole: 'Super Admin RFW',
    description: 'La calle habla por La Kalle. Reggaetón, hip hop, trap y la cultura urbana más auténtica. La emisora que entiende lo que suena en la calle.',
    category: 'Reggaetón / Hip Hop / Trap',
    location: 'Barranquilla, Colombia',
    audience: '1.6M oyentes semanales',
    since: '2009',
    stats: { followers: 1600000, rating: 4.78, interactions: 9800000 },
    benefits: ['Insignia Premium dorada en todos los espacios', 'Prioridad en rankings y búsquedas', 'Acceso anticipado a nuevas funciones', 'Soporte prioritario 24/7', 'Verificación heredada a programas'],
    auditTrail: {
      requestedBy: 'Gerencia La Kalle',
      requestedAt: '2025-01-05',
      reviewedBy: 'Ricardo Montero',
      reviewedAt: '2025-01-20',
      grantedBy: 'Ricardo Montero',
      grantedAt: '2025-01-28',
    },
  },

  // ═══════ PROGRAMAS (6) ═══════
  {
    id: 'ver-prog-001',
    entityType: 'programa',
    entityName: 'El Despertador',
    entitySlug: 'el-despertador',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Morning%20radio%20show%20logo%20warm%20sunrise%20colors%20coffee%20cup%20motif%20alarm%20clock%20graphic%20friendly%20typography%20energetic%20modern%20design&width=200&height=200&seq=ver-despertador&orientation=squarish',
    badgeLevel: 'premium',
    status: 'activa',
    verifiedAt: '2025-04-12T08:00:00Z',
    verifiedAtRelative: 'Abril 2025',
    verifiedBy: 'Ricardo Montero',
    verifiedByRole: 'Super Admin RFW',
    description: 'El morning show más escuchado de Colombia. Noticias, humor, entrevistas y la mejor compañía para empezar el día. 4 horas diarias de radio en vivo que despiertan al país.',
    category: 'Morning Show / Entretenimiento',
    location: 'La Mega 107.3 FM',
    audience: '1.2M oyentes diarios',
    since: '2015',
    stats: { followers: 1200000, rating: 4.91, interactions: 8700000 },
    benefits: ['Insignia Premium dorada', 'Prioridad en programación RFW', 'Estudio virtual exclusivo', 'Estadísticas avanzadas de audiencia'],
    auditTrail: {
      requestedBy: 'Producción El Despertador',
      requestedAt: '2025-03-20',
      reviewedBy: 'Ricardo Montero',
      reviewedAt: '2025-04-05',
      grantedBy: 'Ricardo Montero',
      grantedAt: '2025-04-12',
    },
  },
  {
    id: 'ver-prog-002',
    entityType: 'programa',
    entityName: 'Carrusel Deportivo',
    entitySlug: 'carrusel-deportivo',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20show%20logo%20dynamic%20typography%20soccer%20ball%20graphic%20energy%20waves%20motion%20lines%20competitive%20spirit%20modern%20bold%20design&width=200&height=200&seq=ver-carrusel&orientation=squarish',
    badgeLevel: 'premium',
    status: 'activa',
    verifiedAt: '2025-06-08T09:00:00Z',
    verifiedAtRelative: 'Junio 2025',
    verifiedBy: 'Ricardo Montero',
    verifiedByRole: 'Super Admin RFW',
    description: 'La mejor cobertura deportiva de la radio latinoamericana. Fútbol, béisbol, boxeo y todos los deportes con el análisis más agudo y la pasión del hincha.',
    category: 'Deportes / Análisis',
    location: 'Onda Cero 104.5 FM',
    audience: '890K oyentes por emisión',
    since: '2012',
    stats: { followers: 890000, rating: 4.85, interactions: 6200000 },
    benefits: ['Insignia Premium dorada', 'Prioridad en programación RFW', 'Estadísticas avanzadas de audiencia'],
    auditTrail: {
      requestedBy: 'Equipo Carrusel Deportivo',
      requestedAt: '2025-05-15',
      reviewedBy: 'Ricardo Montero',
      reviewedAt: '2025-06-01',
      grantedBy: 'Ricardo Montero',
      grantedAt: '2025-06-08',
    },
  },
  {
    id: 'ver-prog-003',
    entityType: 'programa',
    entityName: 'Noches de Rock',
    entitySlug: 'noches-de-rock',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Rock%20music%20radio%20show%20logo%20electric%20guitar%20silhouette%20dark%20moody%20background%20neon%20accent%20typography%20vintage%20vinyl%20record%20aesthetic%20modern%20edgy%20design&width=200&height=200&seq=ver-nochesrock&orientation=squarish',
    badgeLevel: 'oficial',
    status: 'activa',
    verifiedAt: '2025-09-15T10:30:00Z',
    verifiedAtRelative: 'Septiembre 2025',
    verifiedBy: 'Carolina Méndez',
    verifiedByRole: 'Super Admin RFW',
    description: 'El programa de rock más auténtico de la radio. Entrevistas a las bandas más importantes, estrenos exclusivos, historia del rock y debate musical sin filtros.',
    category: 'Rock / Música / Cultura',
    location: 'Rock & Pop 95.1 FM',
    audience: '340K oyentes por emisión',
    since: '2017',
    stats: { followers: 340000, rating: 4.72, interactions: 2100000 },
    benefits: ['Insignia Oficial verde', 'Mayor visibilidad en programación'],
    auditTrail: {
      requestedBy: 'Conductor Noches de Rock',
      requestedAt: '2025-08-20',
      reviewedBy: 'Carolina Méndez',
      reviewedAt: '2025-09-10',
      grantedBy: 'Carolina Méndez',
      grantedAt: '2025-09-15',
    },
  },
  {
    id: 'ver-prog-004',
    entityType: 'programa',
    entityName: 'El Mañanero Kalle',
    entitySlug: 'el-mananero-kalle',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Energetic%20morning%20show%20logo%20urban%20style%20bold%20graffiti%20typography%20street%20culture%20sunrise%20gradient%20dynamic%20modern%20youthful%20brand%20identity&width=200&height=200&seq=ver-mananero&orientation=squarish',
    badgeLevel: 'oficial',
    status: 'activa',
    verifiedAt: '2025-07-25T07:00:00Z',
    verifiedAtRelative: 'Julio 2025',
    verifiedBy: 'Carolina Méndez',
    verifiedByRole: 'Super Admin RFW',
    description: 'El morning show más prendido de la costa. Reggaetón, humor caribeño, personajes inolvidables y la energía que necesitás para arrancar el día con toda.',
    category: 'Morning Show / Reggaetón',
    location: 'La Kalle 96.3 FM',
    audience: '620K oyentes diarios',
    since: '2016',
    stats: { followers: 620000, rating: 4.68, interactions: 4500000 },
    benefits: ['Insignia Oficial verde', 'Mayor visibilidad en programación', 'Filtro anti-spam mejorado'],
    auditTrail: {
      requestedBy: 'Producción La Kalle',
      requestedAt: '2025-06-20',
      reviewedBy: 'Carolina Méndez',
      reviewedAt: '2025-07-15',
      grantedBy: 'Carolina Méndez',
      grantedAt: '2025-07-25',
    },
  },
  {
    id: 'ver-prog-005',
    entityType: 'programa',
    entityName: 'Tardes con Valentina',
    entitySlug: 'tardes-con-valentina',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Afternoon%20radio%20show%20logo%20warm%20feminine%20design%20sunset%20palette%20elegant%20typography%20heart%20motif%20soft%20curves%20modern%20inviting%20brand%20identity&width=200&height=200&seq=ver-tardesval&orientation=squarish',
    badgeLevel: 'verificada',
    status: 'activa',
    verifiedAt: '2025-11-10T12:00:00Z',
    verifiedAtRelative: 'Noviembre 2025',
    verifiedBy: 'Andrés Calle',
    verifiedByRole: 'Super Admin RFW',
    description: 'El programa de la tarde que conquistó a Cali. Música tropical, consejos de vida, invitados especiales y la simpatía de Valentina que alegra las tardes.',
    category: 'Variedades / Música Tropical',
    location: 'Tropicalísima 102.7 FM',
    audience: '280K oyentes por emisión',
    since: '2019',
    stats: { followers: 280000, rating: 4.55, interactions: 1800000 },
    benefits: ['Insignia Verificada azul', 'Confianza para nuevos oyentes'],
    auditTrail: {
      requestedBy: 'Valentina Orozco',
      requestedAt: '2025-10-15',
      reviewedBy: 'Andrés Calle',
      reviewedAt: '2025-11-05',
      grantedBy: 'Andrés Calle',
      grantedAt: '2025-11-10',
    },
  },
  {
    id: 'ver-prog-006',
    entityType: 'programa',
    entityName: 'El Show de la Mañana',
    entitySlug: 'el-show-de-la-manana',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Bright%20cheerful%20morning%20radio%20show%20logo%20sunshine%20yellow%20tones%20happy%20typography%20music%20note%20graphics%20warm%20inviting%20modern%20family%20friendly%20brand%20identity&width=200&height=200&seq=ver-showmanana&orientation=squarish',
    badgeLevel: 'verificada',
    status: 'activa',
    verifiedAt: '2025-12-01T08:30:00Z',
    verifiedAtRelative: 'Diciembre 2025',
    verifiedBy: 'Andrés Calle',
    verifiedByRole: 'Super Admin RFW',
    description: 'Tu compañía matutina con buena música, concursos divertidos, recetas, consejos de salud y el mejor ambiente para toda la familia.',
    category: 'Variedades / Familiar',
    location: 'Radio Felicidad 88.9 FM',
    audience: '190K oyentes por emisión',
    since: '2020',
    stats: { followers: 190000, rating: 4.42, interactions: 950000 },
    benefits: ['Insignia Verificada azul', 'Confianza para nuevos oyentes'],
    auditTrail: {
      requestedBy: 'Laura García',
      requestedAt: '2025-11-15',
      reviewedBy: 'Andrés Calle',
      reviewedAt: '2025-11-28',
      grantedBy: 'Andrés Calle',
      grantedAt: '2025-12-01',
    },
  },

  // ═══════ LOCUTORES (6) ═══════
  {
    id: 'ver-loc-001',
    entityType: 'locutor',
    entityName: 'Ricky Navarro',
    entitySlug: 'ricky-navarro',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Charismatic%20Latino%20male%20radio%20host%20mid%20thirties%20warm%20friendly%20smile%20professional%20headshot%20studio%20portrait%20confident%20approachable%20personality&width=200&height=200&seq=ver-ricky&orientation=squarish',
    badgeLevel: 'premium',
    status: 'activa',
    verifiedAt: '2025-02-20T10:00:00Z',
    verifiedAtRelative: 'Febrero 2025',
    verifiedBy: 'Ricardo Montero',
    verifiedByRole: 'Super Admin RFW',
    description: 'La voz más reconocida del rock latinoamericano. Conductor de Noches de Rock, entrevistador nato y referente de la cultura musical en español.',
    category: 'Rock / Música / Entrevistas',
    location: 'Rock & Pop 95.1 FM',
    audience: '340K seguidores',
    since: '2012',
    stats: { followers: 340000, rating: 4.95, interactions: 4200000 },
    benefits: ['Insignia Premium dorada en perfil y cabina', 'Prioridad en búsquedas', 'Verificación de identidad reforzada', 'Protección anti-suplantación'],
    auditTrail: {
      requestedBy: 'Ricky Navarro',
      requestedAt: '2025-02-01',
      reviewedBy: 'Ricardo Montero',
      reviewedAt: '2025-02-15',
      grantedBy: 'Ricardo Montero',
      grantedAt: '2025-02-20',
    },
  },
  {
    id: 'ver-loc-002',
    entityType: 'locutor',
    entityName: 'Laura García',
    entitySlug: 'laura-garcia',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Warm%20friendly%20Latina%20female%20radio%20host%20early%20thirties%20bright%20genuine%20smile%20professional%20headshot%20studio%20portrait%20soft%20lighting%20approachable&width=200&height=200&seq=ver-laura&orientation=squarish',
    badgeLevel: 'premium',
    status: 'activa',
    verifiedAt: '2025-04-05T09:00:00Z',
    verifiedAtRelative: 'Abril 2025',
    verifiedBy: 'Ricardo Montero',
    verifiedByRole: 'Super Admin RFW',
    description: 'La voz más querida de la radio matutina. Conductora de El Show de la Mañana, con una calidez que conquistó a Lima y ahora a toda la comunidad RFW.',
    category: 'Variedades / Familiar / Bienestar',
    location: 'Radio Felicidad 88.9 FM',
    audience: '280K seguidores',
    since: '2014',
    stats: { followers: 280000, rating: 4.88, interactions: 3400000 },
    benefits: ['Insignia Premium dorada en perfil y cabina', 'Prioridad en búsquedas', 'Verificación de identidad reforzada', 'Protección anti-suplantación'],
    auditTrail: {
      requestedBy: 'Laura García',
      requestedAt: '2025-03-20',
      reviewedBy: 'Ricardo Montero',
      reviewedAt: '2025-04-01',
      grantedBy: 'Ricardo Montero',
      grantedAt: '2025-04-05',
    },
  },
  {
    id: 'ver-loc-003',
    entityType: 'locutor',
    entityName: 'Diego Armando Paz',
    entitySlug: 'diego-armando-paz',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latino%20male%20sports%20commentator%20late%20thirties%20sharp%20focused%20expression%20headshot%20studio%20portrait%20clean%20modern%20look%20authoritative%20voice&width=200&height=200&seq=ver-diegopaz&orientation=squarish',
    badgeLevel: 'oficial',
    status: 'activa',
    verifiedAt: '2025-06-18T11:00:00Z',
    verifiedAtRelative: 'Junio 2025',
    verifiedBy: 'Carolina Méndez',
    verifiedByRole: 'Super Admin RFW',
    description: 'La voz del deporte en Onda Cero. Narrador principal de Carrusel Deportivo, con un estilo único que combina pasión, datos y la emoción del fútbol bien contado.',
    category: 'Deportes / Narración / Análisis',
    location: 'Onda Cero 104.5 FM',
    audience: '210K seguidores',
    since: '2013',
    stats: { followers: 210000, rating: 4.75, interactions: 2800000 },
    benefits: ['Insignia Oficial verde en perfil y cabina', 'Verificación de identidad'],
    auditTrail: {
      requestedBy: 'Diego Armando Paz',
      requestedAt: '2025-05-20',
      reviewedBy: 'Carolina Méndez',
      reviewedAt: '2025-06-10',
      grantedBy: 'Carolina Méndez',
      grantedAt: '2025-06-18',
    },
  },
  {
    id: 'ver-loc-004',
    entityType: 'locutor',
    entityName: 'Valentina Orozco',
    entitySlug: 'valentina-orozco',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Vibrant%20young%20Latina%20female%20radio%20host%20mid%20twenties%20bright%20energetic%20smile%20professional%20headshot%20tropical%20colorful%20style%20warm%20personality&width=200&height=200&seq=ver-valentina&orientation=squarish',
    badgeLevel: 'oficial',
    status: 'activa',
    verifiedAt: '2025-08-10T10:30:00Z',
    verifiedAtRelative: 'Agosto 2025',
    verifiedBy: 'Carolina Méndez',
    verifiedByRole: 'Super Admin RFW',
    description: 'El alma de las tardes tropicales. Conductora de Tardes con Valentina, la caleña que contagia alegría con su carisma, su música y su conexión única con la gente.',
    category: 'Música Tropical / Entretenimiento',
    location: 'Tropicalísima 102.7 FM',
    audience: '185K seguidores',
    since: '2018',
    stats: { followers: 185000, rating: 4.62, interactions: 2200000 },
    benefits: ['Insignia Oficial verde en perfil y cabina', 'Verificación de identidad'],
    auditTrail: {
      requestedBy: 'Valentina Orozco',
      requestedAt: '2025-07-15',
      reviewedBy: 'Carolina Méndez',
      reviewedAt: '2025-08-05',
      grantedBy: 'Carolina Méndez',
      grantedAt: '2025-08-10',
    },
  },
  {
    id: 'ver-loc-005',
    entityType: 'locutor',
    entityName: 'El Gordo Martínez',
    entitySlug: 'el-gordo-martinez',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Jolly%20larger%20Latino%20male%20radio%20host%20late%20thirties%20big%20genuine%20laugh%20expressive%20face%20professional%20headshot%20warm%20approachable%20personality%20studio%20portrait&width=200&height=200&seq=ver-gordo&orientation=squarish',
    badgeLevel: 'verificada',
    status: 'activa',
    verifiedAt: '2025-10-20T08:00:00Z',
    verifiedAtRelative: 'Octubre 2025',
    verifiedBy: 'Andrés Calle',
    verifiedByRole: 'Super Admin RFW',
    description: 'El humorista más querido de la radio mañanera. Co-conductor de El Despertador, sus personajes y su ingenio rápido lo convirtieron en un ícono del humor radial.',
    category: 'Humor / Entretenimiento / Morning Show',
    location: 'La Mega 107.3 FM',
    audience: '450K seguidores',
    since: '2008',
    stats: { followers: 450000, rating: 4.82, interactions: 5600000 },
    benefits: ['Insignia Verificada azul en perfil', 'Confianza para la audiencia'],
    auditTrail: {
      requestedBy: 'El Gordo Martínez',
      requestedAt: '2025-09-25',
      reviewedBy: 'Andrés Calle',
      reviewedAt: '2025-10-15',
      grantedBy: 'Andrés Calle',
      grantedAt: '2025-10-20',
    },
  },
  {
    id: 'ver-loc-006',
    entityType: 'locutor',
    entityName: 'La Flaca Hernández',
    entitySlug: 'la-flaca-hernandez',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Stylish%20young%20Latina%20female%20radio%20host%20mid%20twenties%20trendy%20urban%20fashion%20expressive%20face%20professional%20headshot%20bold%20personality%20studio%20portrait&width=200&height=200&seq=ver-flaca&orientation=squarish',
    badgeLevel: 'verificada',
    status: 'activa',
    verifiedAt: '2025-11-25T09:30:00Z',
    verifiedAtRelative: 'Noviembre 2025',
    verifiedBy: 'Andrés Calle',
    verifiedByRole: 'Super Admin RFW',
    description: 'La voz fresca de la radio urbana. Co-conductora de El Mañanero Kalle, con un flow auténtico que conecta con la nueva generación de oyentes.',
    category: 'Reggaetón / Cultura Urbana / Moda',
    location: 'La Kalle 96.3 FM',
    audience: '320K seguidores',
    since: '2019',
    stats: { followers: 320000, rating: 4.65, interactions: 3200000 },
    benefits: ['Insignia Verificada azul en perfil', 'Confianza para la audiencia'],
    auditTrail: {
      requestedBy: 'La Flaca Hernández',
      requestedAt: '2025-11-01',
      reviewedBy: 'Andrés Calle',
      reviewedAt: '2025-11-20',
      grantedBy: 'Andrés Calle',
      grantedAt: '2025-11-25',
    },
  },

  // ═══════ PATROCINADORES (5) ═══════
  {
    id: 'ver-pat-001',
    entityType: 'patrocinador',
    entityName: 'Cerveza Águila',
    entitySlug: 'cerveza-aguila',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Premium%20beer%20brand%20logo%20golden%20eagle%20emblem%20bold%20typography%20rich%20amber%20tones%20sophisticated%20corporate%20identity%20modern%20clean%20design&width=200&height=200&seq=ver-aguila&orientation=squarish',
    badgeLevel: 'premium',
    status: 'activa',
    verifiedAt: '2025-03-01T10:00:00Z',
    verifiedAtRelative: 'Marzo 2025',
    verifiedBy: 'Ricardo Montero',
    verifiedByRole: 'Super Admin RFW',
    description: 'Patrocinador Premium de RFW. Apoya los eventos más importantes de la comunidad: RFW Fest, Premios RFW, Copas RFW y múltiples concursos y programas.',
    category: 'Bebidas / Consumo Masivo',
    location: 'Colombia',
    stats: { followers: 520000, rating: 4.85, interactions: 8900000 },
    benefits: ['Insignia Premium dorada en todos los espacios patrocinados', 'Prioridad en búsqueda de patrocinadores', 'Dashboard de métricas avanzado', 'Espacio destacado en eventos RFW'],
    auditTrail: {
      requestedBy: 'Marketing Cerveza Águila',
      requestedAt: '2025-02-10',
      reviewedBy: 'Ricardo Montero',
      reviewedAt: '2025-02-25',
      grantedBy: 'Ricardo Montero',
      grantedAt: '2025-03-01',
    },
  },
  {
    id: 'ver-pat-002',
    entityType: 'patrocinador',
    entityName: 'Movistar',
    entitySlug: 'movistar',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Telecommunications%20company%20logo%20modern%20minimalist%20design%20green%20gradients%20digital%20connection%20motif%20clean%20corporate%20identity%20professional%20tech%20brand&width=200&height=200&seq=ver-movistar&orientation=squarish',
    badgeLevel: 'premium',
    status: 'activa',
    verifiedAt: '2025-05-10T09:00:00Z',
    verifiedAtRelative: 'Mayo 2025',
    verifiedBy: 'Ricardo Montero',
    verifiedByRole: 'Super Admin RFW',
    description: 'Partner tecnológico de RFW. Patrocina la conectividad de los eventos más grandes y apoya la innovación en la plataforma de radio digital.',
    category: 'Telecomunicaciones / Tecnología',
    location: 'Latinoamérica',
    stats: { followers: 780000, rating: 4.72, interactions: 12000000 },
    benefits: ['Insignia Premium dorada en todos los espacios patrocinados', 'Prioridad en búsqueda de patrocinadores', 'Dashboard de métricas avanzado'],
    auditTrail: {
      requestedBy: 'Alianzas Movistar',
      requestedAt: '2025-04-15',
      reviewedBy: 'Ricardo Montero',
      reviewedAt: '2025-05-05',
      grantedBy: 'Ricardo Montero',
      grantedAt: '2025-05-10',
    },
  },
  {
    id: 'ver-pat-003',
    entityType: 'patrocinador',
    entityName: 'Coca-Cola',
    entitySlug: 'coca-cola',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Iconic%20global%20beverage%20brand%20logo%20classic%20red%20white%20typography%20dynamic%20ribbon%20design%20timeless%20corporate%20identity%20recognizable%20modern&width=200&height=200&seq=ver-cocacola&orientation=squarish',
    badgeLevel: 'oficial',
    status: 'activa',
    verifiedAt: '2025-07-12T11:00:00Z',
    verifiedAtRelative: 'Julio 2025',
    verifiedBy: 'Carolina Méndez',
    verifiedByRole: 'Super Admin RFW',
    description: 'Patrocinador Oficial de múltiples programas y concursos en RFW. Apoya especialmente los eventos de música y entretenimiento familiar.',
    category: 'Bebidas / Consumo Masivo',
    location: 'Global',
    stats: { followers: 1200000, rating: 4.90, interactions: 25000000 },
    benefits: ['Insignia Oficial verde en espacios patrocinados', 'Visibilidad en concursos y eventos'],
    auditTrail: {
      requestedBy: 'Marketing Coca-Cola LATAM',
      requestedAt: '2025-06-20',
      reviewedBy: 'Carolina Méndez',
      reviewedAt: '2025-07-08',
      grantedBy: 'Carolina Méndez',
      grantedAt: '2025-07-12',
    },
  },
  {
    id: 'ver-pat-004',
    entityType: 'patrocinador',
    entityName: 'Samsung',
    entitySlug: 'samsung',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Global%20technology%20brand%20logo%20minimalist%20modern%20design%20deep%20blue%20tones%20sleek%20typography%20innovation%20motif%20professional%20corporate%20identity&width=200&height=200&seq=ver-samsung&orientation=squarish',
    badgeLevel: 'verificada',
    status: 'activa',
    verifiedAt: '2025-09-08T10:00:00Z',
    verifiedAtRelative: 'Septiembre 2025',
    verifiedBy: 'Andrés Calle',
    verifiedByRole: 'Super Admin RFW',
    description: 'Patrocinador tecnológico de los Premios RFW y el Estudio Virtual. Provee equipos y soporte para las transmisiones de alta calidad.',
    category: 'Tecnología / Electrónica',
    location: 'Global',
    stats: { followers: 650000, rating: 4.68, interactions: 8900000 },
    benefits: ['Insignia Verificada azul', 'Confianza para la comunidad'],
    auditTrail: {
      requestedBy: 'Samsung Electronics LATAM',
      requestedAt: '2025-08-15',
      reviewedBy: 'Andrés Calle',
      reviewedAt: '2025-09-01',
      grantedBy: 'Andrés Calle',
      grantedAt: '2025-09-08',
    },
  },
  {
    id: 'ver-pat-005',
    entityType: 'patrocinador',
    entityName: 'Grupo Bancolombia',
    entitySlug: 'grupo-bancolombia',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Financial%20institution%20brand%20logo%20bold%20professional%20typography%20warm%20corporate%20tones%20trust%20building%20design%20modern%20banking%20identity%20clean&width=200&height=200&seq=ver-bancolombia&orientation=squarish',
    badgeLevel: 'verificada',
    status: 'activa',
    verifiedAt: '2025-10-15T08:00:00Z',
    verifiedAtRelative: 'Octubre 2025',
    verifiedBy: 'Andrés Calle',
    verifiedByRole: 'Super Admin RFW',
    description: 'Aliado financiero de la comunidad RFW. Patrocina programas de educación financiera, becas para locutores emergentes y los premios a la innovación radial.',
    category: 'Banca / Finanzas',
    location: 'Colombia',
    stats: { followers: 380000, rating: 4.55, interactions: 4200000 },
    benefits: ['Insignia Verificada azul', 'Confianza para la comunidad'],
    auditTrail: {
      requestedBy: 'RSE Grupo Bancolombia',
      requestedAt: '2025-09-20',
      reviewedBy: 'Andrés Calle',
      reviewedAt: '2025-10-10',
      grantedBy: 'Andrés Calle',
      grantedAt: '2025-10-15',
    },
  },

  // ═══════ EMPRESAS (5) ═══════
  {
    id: 'ver-emp-001',
    entityType: 'empresa',
    entityName: 'AudioPro Studio',
    entitySlug: 'audiopro-studio',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Professional%20audio%20equipment%20company%20logo%20sound%20wave%20motif%20studio%20monitor%20graphic%20clean%20modern%20typography%20dark%20background%20tech%20professional%20brand&width=200&height=200&seq=ver-audiopro&orientation=squarish',
    badgeLevel: 'premium',
    status: 'activa',
    verifiedAt: '2025-04-20T10:00:00Z',
    verifiedAtRelative: 'Abril 2025',
    verifiedBy: 'Ricardo Montero',
    verifiedByRole: 'Super Admin RFW',
    description: 'Proveedor líder de equipos de audio profesional para radios en RFW. Más de 200 estudios equipados, soporte 24/7 y los mejores precios para la comunidad radial.',
    category: 'Equipos de Audio / Tecnología',
    location: 'Bogotá, Colombia',
    stats: { followers: 89000, rating: 4.82, interactions: 1200000 },
    benefits: ['Insignia Premium dorada', 'Directorio destacado para radios', 'Soporte prioritario', 'Ofertas exclusivas para miembros RFW'],
    auditTrail: {
      requestedBy: 'CEO AudioPro Studio',
      requestedAt: '2025-03-25',
      reviewedBy: 'Ricardo Montero',
      reviewedAt: '2025-04-15',
      grantedBy: 'Ricardo Montero',
      grantedAt: '2025-04-20',
    },
  },
  {
    id: 'ver-emp-002',
    entityType: 'empresa',
    entityName: 'StreamingPro LATAM',
    entitySlug: 'streamingpro-latam',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Streaming%20technology%20company%20logo%20modern%20minimalist%20design%20play%20button%20motif%20vibrant%20gradient%20tones%20digital%20sleek%20professional%20brand%20identity&width=200&height=200&seq=ver-streamingpro&orientation=squarish',
    badgeLevel: 'oficial',
    status: 'activa',
    verifiedAt: '2025-07-30T12:00:00Z',
    verifiedAtRelative: 'Julio 2025',
    verifiedBy: 'Carolina Méndez',
    verifiedByRole: 'Super Admin RFW',
    description: 'Plataforma de streaming profesional para radios. Servidores dedicados, calidad 320kbps, sin límite de oyentes simultáneos. El aliado técnico de +500 emisoras en RFW.',
    category: 'Streaming / Cloud / Tecnología',
    location: 'São Paulo, Brasil',
    stats: { followers: 125000, rating: 4.70, interactions: 2400000 },
    benefits: ['Insignia Oficial verde', 'Directorio para radios', 'Soporte técnico prioritario'],
    auditTrail: {
      requestedBy: 'StreamingPro LATAM',
      requestedAt: '2025-06-25',
      reviewedBy: 'Carolina Méndez',
      reviewedAt: '2025-07-25',
      grantedBy: 'Carolina Méndez',
      grantedAt: '2025-07-30',
    },
  },
  {
    id: 'ver-emp-003',
    entityType: 'empresa',
    entityName: 'Voces y Talentos',
    entitySlug: 'voces-y-talentos',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Talent%20agency%20logo%20creative%20design%20microphone%20silhouette%20elegant%20typography%20warm%20gold%20tones%20artistic%20professional%20show%20business%20brand%20identity&width=200&height=200&seq=ver-voces&orientation=squarish',
    badgeLevel: 'verificada',
    status: 'activa',
    verifiedAt: '2025-09-22T09:00:00Z',
    verifiedAtRelative: 'Septiembre 2025',
    verifiedBy: 'Andrés Calle',
    verifiedByRole: 'Super Admin RFW',
    description: 'Agencia de talentos especializada en locución y producción radial. Descubrimiento, formación y colocación de nuevos talentos en las mejores radios de RFW.',
    category: 'Agencia de Talento / Formación',
    location: 'Medellín, Colombia',
    stats: { followers: 67000, rating: 4.65, interactions: 980000 },
    benefits: ['Insignia Verificada azul', 'Confianza para locutores y radios'],
    auditTrail: {
      requestedBy: 'Voces y Talentos',
      requestedAt: '2025-08-30',
      reviewedBy: 'Andrés Calle',
      reviewedAt: '2025-09-18',
      grantedBy: 'Andrés Calle',
      grantedAt: '2025-09-22',
    },
  },
  {
    id: 'ver-emp-004',
    entityType: 'empresa',
    entityName: 'RadioMarketing Digital',
    entitySlug: 'radiomarketing-digital',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Digital%20marketing%20agency%20logo%20modern%20creative%20design%20megaphone%20motif%20analytics%20graph%20vibrant%20colors%20professional%20sleek%20brand%20identity%20innovative&width=200&height=200&seq=ver-radiomkt&orientation=squarish',
    badgeLevel: 'verificada',
    status: 'activa',
    verifiedAt: '2025-10-28T10:00:00Z',
    verifiedAtRelative: 'Octubre 2025',
    verifiedBy: 'Andrés Calle',
    verifiedByRole: 'Super Admin RFW',
    description: 'Agencia de marketing digital especializada en el ecosistema radial. Estrategias de crecimiento, monetización y posicionamiento para emisoras en RFW.',
    category: 'Marketing Digital / Crecimiento',
    location: 'Ciudad de México, México',
    stats: { followers: 54000, rating: 4.58, interactions: 750000 },
    benefits: ['Insignia Verificada azul', 'Confianza para radios'],
    auditTrail: {
      requestedBy: 'RadioMarketing Digital',
      requestedAt: '2025-10-01',
      reviewedBy: 'Andrés Calle',
      reviewedAt: '2025-10-25',
      grantedBy: 'Andrés Calle',
      grantedAt: '2025-10-28',
    },
  },
  {
    id: 'ver-emp-005',
    entityType: 'empresa',
    entityName: 'Eventos Sonoros SAS',
    entitySlug: 'eventos-sonoros-sas',
    entityPhotoUrl: 'https://readdy.ai/api/search-image?query=Event%20production%20company%20logo%20dynamic%20design%20stage%20lighting%20motif%20vibrant%20colors%20professional%20typography%20show%20business%20entertainment%20identity%20creative&width=200&height=200&seq=ver-eventosson&orientation=squarish',
    badgeLevel: 'oficial',
    status: 'activa',
    verifiedAt: '2025-11-15T11:00:00Z',
    verifiedAtRelative: 'Noviembre 2025',
    verifiedBy: 'Carolina Méndez',
    verifiedByRole: 'Super Admin RFW',
    description: 'Productora de eventos oficial de RFW. Organización de RFW Fest, Premios RFW y Copas RFW. Producción impecable, logística de primer nivel y experiencias inolvidables.',
    category: 'Producción de Eventos / Logística',
    location: 'Bogotá, Colombia',
    stats: { followers: 92000, rating: 4.78, interactions: 1800000 },
    benefits: ['Insignia Oficial verde', 'Directorio destacado', 'Prioridad en contrataciones RFW'],
    auditTrail: {
      requestedBy: 'CEO Eventos Sonoros',
      requestedAt: '2025-10-20',
      reviewedBy: 'Carolina Méndez',
      reviewedAt: '2025-11-10',
      grantedBy: 'Carolina Méndez',
      grantedAt: '2025-11-15',
    },
  },
];

export const verificationBenefits = [
  {
    id: 1,
    title: 'Más Confianza',
    icon: 'ri-shield-check-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    description: 'La insignia de verificación es un sello de autenticidad. Oyentes, patrocinadores y otras radios saben que están interactuando con una entidad legítima, verificada por RFW. La confianza se traduce en más seguidores, más interacciones y más oportunidades.',
    bulletPoints: ['Identidad confirmada por Super Admins', 'Protección contra suplantación', 'Credibilidad instantánea ante la comunidad', 'Diferenciación clara de cuentas falsas'],
  },
  {
    id: 2,
    title: 'Más Visibilidad',
    icon: 'ri-eye-line',
    color: 'text-sky-600',
    bgColor: 'bg-sky-50',
    description: 'Las entidades verificadas aparecen primero en búsquedas, rankings y recomendaciones. La insignia llama la atención en cabinas, perfiles y listados. Ser verificado/a en RFW es ser más fácil de encontrar.',
    bulletPoints: ['Prioridad en resultados de búsqueda', 'Destacado en rankings y listados', 'Badge visible en cabina, perfil y eventos', 'Mayor alcance orgánico en la plataforma'],
  },
  {
    id: 3,
    title: 'Mayor Prioridad',
    icon: 'ri-rocket-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    description: 'Los verificados acceden primero a nuevas funciones, tienen soporte prioritario y sus reportes y solicitudes se procesan antes. En RFW, la verificación es un carril rápido hacia el crecimiento.',
    bulletPoints: ['Acceso anticipado a nuevas funciones', 'Soporte prioritario 24/7', 'Procesamiento prioritario de reportes', 'Invitación a eventos exclusivos RFW'],
  },
];

export const verificationRules = [
  {
    id: 1,
    title: 'Solo SA puede verificar',
    description: 'Únicamente los Super Admins de RFW tienen la autoridad para otorgar, modificar o revocar una verificación. Ningún moderador, administrador de radio ni usuario puede otorgar insignias de verificación. Esto garantiza que cada insignia representa una revisión rigurosa y centralizada.',
    icon: 'ri-admin-line',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
  },
  {
    id: 2,
    title: 'Verificación basada en mérito',
    description: 'La verificación no se compra ni se regala. Se otorga con base en: autenticidad de la entidad, trayectoria comprobable, comunidad activa, cumplimiento de normas RFW y calidad de contenido. Cada solicitud pasa por un proceso de revisión documentado.',
    icon: 'ri-medal-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
  },
  {
    id: 3,
    title: 'Auditoría completa e inmutable',
    description: 'Cada verificación deja un rastro completo: quién la solicitó, quién la revisó, quién la otorgó y cuándo. Este registro es inmutable y auditable. Si una entidad pierde los estándares, la verificación puede ser revocada con el mismo nivel de trazabilidad.',
    icon: 'ri-file-search-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
  },
];

export function getVerifiedByType(type: VerificationEntityType | 'todas'): VerifiedEntity[] {
  if (type === 'todas') return verifiedEntities;
  return verifiedEntities.filter((e) => e.entityType === type);
}

export function getVerifiedByBadge(level: VerificationBadgeLevel | 'todas'): VerifiedEntity[] {
  if (level === 'todas') return verifiedEntities;
  return verifiedEntities.filter((e) => e.badgeLevel === level);
}